import React, {
  useState,
  useMemo,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
// import "../../css/table.css";
import SearchBox from './searchBox';
import PaginationControls from './pagination';
import Table from './table';
import ModalManager from './modals/tableModalManager';
import { callAPI, getTableFromDatabase } from '../utils';
import Tag from '../ui/tags';
import {
  Stack,
} from '@mui/material';
import Button from '../ui/button';
import Dialog from '../ui/dialog';
import ViewsModal from './modals/viewsModal';

/**
 * Custom hook to manage modal state and data.
 *
 * @param {string | null} temp - The default template for the modal.
 * @returns {object} - Contains state and functions to control modal behavior.
 */
const useModal = (temp = null) => {
  const [data, setData] = useState(null);
  const [template, setTemplate] = useState(temp);
  const [defaultTemplate, setDefaultTemplate] = useState(temp);

  useEffect(() => {
    if (template === defaultTemplate) return;
    setData(null);
    setTemplate(defaultTemplate);
  }, [defaultTemplate]);

  return {
    data,
    template,
    setDefaultTemplate,
    setTemplate,
    openModal: (newTemplate, newData = data) => {
      setTemplate(newTemplate);
      setData(newData);
    },
    closeModal: () => {
      setData(null);
      setTemplate(defaultTemplate);
    },
  };
};

/**
 * DataTable component that displays a paginated and searchable table with customizable views.
 * It fetches the table data from a database, processes it, and provides features such as searching,
 * pagination, and modal management for viewing row details or saving custom views.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.tableId - The ID of the table to fetch from the database.
 * @param {Object} [props.callbacks] - Optional callback functions for custom actions.
 * @returns {JSX.Element} The rendered DataTable component.
 */
const DataTable = forwardRef(
  (
    { tableId, callbacks = {}, actionButton = null, simpleView = false, hideView = false },
    ref
  ) => {
    const [searchTerm, setSearchTerm] = useState(''); // Search query for filtering data.
    const [currentPage, setCurrentPage] = useState(1); // Current page number for pagination.
    const [rowsPerPage, setRowsPerPage] = useState(10); // Number of rows to display per page.
    const [tableData, setTableData] = useState({
      data: [],
      headers: [],
      title: '',
      noDataMsg: '',
      modalTemplate: null,
    });
    const [views, setViews] = useState([]); // List of saved views.
    const [columns, setColumns] = useState([]); // Table columns with metadata.
    const [visibleColumns, setVisibleColumns] = useState([]); // Columns currently visible in the table.
    const [formattedHeaders, setFormattedHeaders] = useState();
    const [openSidePeek, setOpenSidePeek] = useState(false);

    const {
      data: modalData,
      template: modalTemplate,
      setDefaultTemplate,
      setTemplate,
      openModal,
      closeModal,
    } = useModal(tableData.modalTemplate);

    useImperativeHandle(ref, () => ({
      setTemplate,
      openModal,
      closeModal,
    }));

    useEffect(() => {
      const fetchData = async () => {
        try {
          // Fetch table data from the database
          const tableResponse = await getTableFromDatabase(tableId);

          if (tableResponse) {
            setTableData({
              data: tableResponse.data,
              headers: tableResponse.headers,
              title: tableResponse.title,
              noDataMsg: tableResponse.noDataMsg,
              modalTemplate: tableResponse.modalTemplate,
            });
            setDefaultTemplate(tableResponse.modalTemplate);
          } else {
            setTableData({
              data: [],
              headers: [],
              noDataMsg: 'An unknown error occured when fetching table',
            });
          }

          const formatted = Object.entries(tableResponse.headers || {}).map(
            ([key, value]) => ({
              header: value.formattedName,
              accessor: key,
              type: value.dataType,
              enumValues: value.enumValues,
              isVisible: value.isVisible ?? true,
              filter: '',
            })
          );
          setFormattedHeaders(formatted);

          // Fetch saved views for the table
          callAPI({
            request: 'views/getView',
            method: 'get',
            params: { tableId },
            onSuccess: (viewData) => {
              const newViews = viewData.data.map((apiView) => ({
                ...JSON.parse(apiView.viewSettings),
                name: apiView.viewName,
                id: apiView.viewId,
                isGlobal: apiView.userId === -1,
              }));

              // Set default view
              setViews((prevViews) => [
                {
                  name: 'Default',
                  id: 0,
                  isGlobal: false,
                  visibleColumns: formatted,
                  searchTerm: '',
                },
                ...newViews,
              ]);
            },
            onError: (err) => console.error('Error fetching views:', err),
          });
        } catch (error) {
          console.error('Error fetching table data:', error);
        }
      };

      fetchData();
    }, [tableId]);

    useEffect(() => {
      if (formattedHeaders) {
        setColumns(formattedHeaders);
      }
    }, [formattedHeaders]);

    useEffect(() => {
      if (columns.length > 0) {
        setVisibleColumns(columns.filter((col) => col.isVisible));
      }
    }, [columns]);

    /**
     * Processes a cell value based on its column definition.
     *
     * @param {*} cell - The cell to process.
     * @param {object} column - The column definition containing type and enum values.
     * @returns {*} - The processed cell value.
     */
    const processCellValue = (cell, column) => {
      if (
        Array.isArray(cell) &&
        cell.every(
          (item) => typeof item === 'object' && item.hasOwnProperty('label')
        )
      ) {
        if (
          cell.every((item) => {
            return item?.data && item.data.hasOwnProperty('colour');
          })
        ) {
          return cell.map((item) => (
            <Tag
              key={item.value}
              label={item.label}
              colour={item.data.colour}
            />
          ));
        }

        return cell.map((item) => item.label).join(', ');
      }

      let value = cell?.label;
      if (value == null) return;

      if (
        column.enumValues &&
        Array.isArray(column.enumValues) &&
        column.enumValues.length > 0
      ) {
        const matchingEnum = column.enumValues.find(
          (item) => String(item.value) === String(value).toLowerCase()
        );
        return matchingEnum ? matchingEnum.label : 'N/A';
      }
      if (column.type === 'tinyint') {
        return value === 0 ? 'False' : value === 1 ? 'True' : 'N/A';
      }

      if (cell.data && cell.data.colour) {
        return (
          <Tag key={cell.value} label={cell.label} colour={cell.data.colour} />
        );
      }

      return value ?? 'N/A';
    };

    const [viewsModalOpen, setViewsModalOpen] = useState(false);

    /**
     * Processes the entire data set based on column definitions and cell processing rules.
     *
     * @returns {Array} - Processed table data.
     */
    const processedData = useMemo(() => {
      return tableData.data.map((row) =>
        columns.reduce((formattedRow, column) => {
          formattedRow[column.accessor] = processCellValue(
            row[column.accessor],
            column
          );
          return formattedRow;
        }, {})
      );
    }, [tableData.data, columns]);

    /**
     * Filters the data based on the search term and visible columns.
     *
     * @returns {Array} - Filtered table data.
     */
    const filteredData = useMemo(() => {
      const search = searchTerm.toLowerCase();
    
      return processedData.filter((row) => {
        return visibleColumns.some((col) => {
          const cellValue = String(row[col.accessor]).toLowerCase();
    
          if (col.filter) {
            if (col.type === 'date') {
              if (cellValue.toLowerCase() === 'n/a') return false;
    
              if (col.filter.start === '' || col.filter.end === '') return true;
    
              const cellDate = new Date(cellValue);
              const startDate = new Date(col.filter.start);
              const endDate = new Date(col.filter.end);
    
              return cellDate >= startDate && cellDate <= endDate;
            }
            const filterValue = String(col.filter).toLowerCase();
    
            if (!cellValue.includes(filterValue)) return false
          }

          return cellValue.includes(search);
        });
      });
    }, [processedData, visibleColumns, searchTerm]);
    

    /**
     * Paginates the filtered data based on the current page and rows per page.
     *
     * @returns {Array} - Paginated data for the current page.
     */
    const paginatedData = useMemo(() => {
      if (rowsPerPage === 'All') return filteredData;
      const start = (currentPage - 1) * rowsPerPage;
      return filteredData.slice(start, start + rowsPerPage);
    }, [filteredData, currentPage, rowsPerPage]);

    const totalPages =
      rowsPerPage === 'All' ? 1 : Math.ceil(filteredData.length / rowsPerPage);

    /**
     * Applies a saved view to the table by updating the columns to the visible columns of the view.
     *
     * @param {object} view - The view to apply.
     */
    const applyView = (view) => {
      console.log(view);
      setColumns(view.visibleColumns);
    };

    /**
     * Saves a new or edited view.
     *
     * @param {object} newView - The new or edited view to save.
     * @param {boolean} isEditing - Flag indicating whether the view is being edited.
     */
    const saveView = (newView, isEditing) => {
      callAPI({
        request: 'views/saveView',
        method: 'post',
        returnSimpleError: true,
        params: {
          tableId: tableId,
          viewData: newView,
        },
        onSuccess: (data) => {
          setViews((prevViews) =>
            isEditing
              ? prevViews.map((view) =>
                  view.name === newView.name ? newView : view
                )
              : [...prevViews, newView]
          );
        },
        onError: (error) => {
          console.error('Error saving view:', error);
        },
      });
    };

    /**
     * Deletes a saved view by name.
     *
     * @param {string} viewName - The name of the view to delete.
     * @param {boolean} isGlobal - Flag indicating whether the view is global.
     */
    const deleteView = (viewName, isGlobal) => {
      callAPI({
        request: 'views/deleteView',
        method: 'post',
        params: {
          tableId: tableId,
          viewName: viewName,
          isGlobal: isGlobal,
        },
        onSuccess: (data) => {
          if (viewName !== 'Default') {
            setViews((prev) =>
              prev.filter(
                (view) => !(view.name === viewName && view.isGlobal == isGlobal)
              )
            );
          }
        },
        onError: (error) => {
          console.error('Error deleting view:', error);
        },
      });
    };

    // Merge custom callbacks with internal callbacks
    const mergedCallbacks = {
      ...callbacks,
      onClose: closeModal,
      saveView,
      deleteView,
      applyView,
      setModalTemplate: setTemplate,
      getColumns: () => columns,
      setColumns,
      getViews: () => views,
      getData: () => tableData,
      updateRow: (updatedRow, header, mode) => {
        if (!tableData.headers[header]) return;

        setTableData((prevTableData) => {
          const rowIndex = prevTableData.data.findIndex((row) => {
            if (row[header]?.value === null) return false;
            return row[header]?.value === updatedRow[header];
          });

          let updatedData;
          if (mode === 'modify') {
            // Modify mode: update the row if found, or add it if not found
            if (rowIndex === -1) {
              updatedData = [...prevTableData.data, updatedRow];
            } else {
              updatedData = [...prevTableData.data];
              updatedData[rowIndex] = {
                ...updatedData[rowIndex],
                ...updatedRow,
              };
            }
          } else if (mode === 'delete') {
            // Delete mode: remove the row if found
            if (rowIndex !== -1) {
              updatedData = prevTableData.data.filter(
                (_, index) => index !== rowIndex
              );
            } else {
              updatedData = [...prevTableData.data];
            }
          } else {
            // If mode is not recognized, return the original data
            updatedData = [...prevTableData.data];
          }

          return { ...prevTableData, data: updatedData };
        });
      },
    };

    const handleRowClick = (data) => {
      if (modalTemplate == null) return;
      openModal(modalTemplate, data);
      setOpenSidePeek(true);
    };

    // Render table if valid table ID exists
    if (!tableId) return <p>Table does not have an ID</p>;

    return (
      <Stack display="flex" spacing={3}>
        {/* <h2>{tableData.title}</h2> */}

        {!simpleView && (
          <Stack
            display="flex"
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            {/* To DO: Remove flexgrow when sort, filter buttons are available */}
            <Stack
              display="flex"
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ flexGrow: 0.2 }}
            >
              <SearchBox
                searchTerm={searchTerm}
                onSearch={setSearchTerm}
                visibleColumns={visibleColumns}
              />
              {!hideView && (
              <Button
                variant="outlined"
                // onClick={() =>
                //   openModal('viewsModal', {
                //     views,
                //     columns,
                //     callbacks: mergedCallbacks,
                //   })
                // }
                onClick={() => setViewsModalOpen(true)}
                sx={{ padding: '10px' }}
              >
                Views
              </Button>
              )}
              <Dialog open={viewsModalOpen} onClose={() => setViewsModalOpen(false)} title="Manage Views" maxWidth='sm' hideCancel hideConfirm>
              <ViewsModal
                    data={{ views, columns }}
                    callbacks={mergedCallbacks}
                  />
              </Dialog>
            </Stack>
            {!simpleView && actionButton}
            </Stack>
        )}

        <Table
          data={paginatedData}
          columns={visibleColumns}
          noDataMsg={tableData.noDataMsg}
          onRowClick={handleRowClick}
        />

        {!simpleView && (
          <Stack direction="row" justifyContent="flex-end">
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              rowsPerPage={rowsPerPage}
              onPreviousPage={() =>
                setCurrentPage((prev) => Math.max(prev - 1, 1))
              }
              onNextPage={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              onRowsPerPageChange={(value) => {
                setRowsPerPage(value === 'All' ? 'All' : Number(value));
                if (value === 'All') setCurrentPage(1);
              }}
            />
          </Stack>
        )}

        {modalData && modalTemplate && (
          <ModalManager
            data={{ ...modalData, reloadOnSuccess: true }}
            template={modalTemplate}
            callbacks={mergedCallbacks}
            open={openSidePeek}
          />
        )}
      </Stack>
    );
  }
);

export default DataTable;
